import React, { useCallback, useEffect, useState } from 'react'
import { useAuth } from '../auth/AuthContext'
import { api } from '../lib/api'
import { useToast } from '../ui/ToastProvider'
import LibraryMappedImagesModal from '../ui/LibraryMappedImagesModal'
import { formatDateTimeMinute } from '../lib/date'
import MenuGuideButton from '../components/MenuGuideButton'

/** 템플릿의 설계사 자리표시자를 로그인 사용자 이름으로 치환한 뒤 복사용 문자열로 만듭니다. */
function applyPlannerNameToMessageTemplate(text, plannerName) {
  const n = (plannerName || '').trim()
  if (!text) return ''
  if (!n) return text
  let s = String(text)
  s = s.split('{설계사}').join(n)
  s = s.split('#{plannerName}').join(n)
  s = s.split('#{agentName}').join(n)
  s = s.split('#{설계사명}').join(n)
  s = s.split('보험전문가 OOO').join(`보험전문가 ${n}`)
  s = s.split('OOO').join(n)
  return s
}

export default function KakaoBulkSenderPage() {
  const { show } = useToast()
  const { user } = useAuth()
  const [versionInfo, setVersionInfo] = useState(null)
  const [versionLoading, setVersionLoading] = useState(false)
  const [templates, setTemplates] = useState([])
  const [templatesLoading, setTemplatesLoading] = useState(false)
  const [templateCategory, setTemplateCategory] = useState('')
  const [templateCategories, setTemplateCategories] = useState([])
  const [manualOpen, setManualOpen] = useState(false)
  const [selectedManual, setSelectedManual] = useState(null)
  const [installCodeOpen, setInstallCodeOpen] = useState(false)
  const [installCodeLoading, setInstallCodeLoading] = useState(false)
  const [installCodeInfo, setInstallCodeInfo] = useState(null)
  const [templatePreview, setTemplatePreview] = useState(null)
  const [templateImageView, setTemplateImageView] = useState(null)

  useEffect(() => {
    loadVersionInfo()
    loadTemplateCategories()
  }, [])

  const loadVersionInfo = async () => {
    setVersionLoading(true)
    try {
      const res = await api.get('/public/external/versions/kakao-sender/latest')
      const data = res?.data?.data ?? res?.data
      console.log('버전 정보 응답:', data)
      console.log('설치 가이드 파일 ID:', data?.installationManualFileId)
      console.log('사용법 가이드 파일 ID:', data?.usageManualFileId)
      setVersionInfo(data)
    } catch (e) {
      console.error('버전 정보 조회 실패:', e)
      // 버전 조회 실패해도 페이지는 계속 표시
    } finally {
      setVersionLoading(false)
    }
  }

  const loadTemplateCategories = async () => {
    try {
      const res = await api.get('/operator/message-templates/approved/categories')
      const payload = res?.data?.data ?? res?.data
      setTemplateCategories(Array.isArray(payload) ? payload : [])
    } catch (e) {
      console.error('템플릿 카테고리 조회 실패:', e)
      setTemplateCategories([])
    }
  }

  const loadTemplates = useCallback(async () => {
    setTemplatesLoading(true)
    try {
      const params = { page: 0, size: 100 }
      if (templateCategory) params.category = templateCategory
      // 운영자용 API 활용 (일반 사용자는 빈 목록 또는 에러 처리)
      const res = await api.get('/operator/message-templates/approved', {
        params
      })
      const payload = res?.data?.data ?? res?.data
      const items = payload?.content || payload?.items || []
      // 승인된 메시지 템플릿은 templateType 이 INFO/CONSULT/REMIND 뿐이며 KAKAO 값은 없음.
      // 외부 단체발송 프로그램 API와 동일하게 승인 전체를 노출한다.
      setTemplates(Array.isArray(items) ? items : [])
    } catch (e) {
      // 템플릿 조회 실패해도 계속 진행 (권한 없을 수 있음)
      console.error('템플릿 목록 조회 실패:', e)
      setTemplates([])
    } finally {
      setTemplatesLoading(false)
    }
  }, [templateCategory])

  useEffect(() => {
    loadTemplates()
  }, [loadTemplates])

  const handleDownload = () => {
    if (versionInfo?.downloadUrl) {
      window.open(versionInfo.downloadUrl, '_blank', 'noopener,noreferrer')
    } else {
      show('다운로드 링크를 찾을 수 없습니다.', 'error')
    }
  }

  const copyTemplateBody = async (template) => {
    const raw = template?.content != null ? String(template.content) : ''
    const withName = applyPlannerNameToMessageTemplate(raw, user?.name || '')
    try {
      await navigator.clipboard.writeText(withName)
      show(user?.name ? '본문을 복사했습니다. (설계사 이름이 반영되었습니다)' : '본문을 복사했습니다.', 'success')
    } catch {
      show('복사에 실패했습니다.', 'error')
    }
  }

  const formatFileSize = (bytes) => {
    if (!bytes) return '-'
    if (bytes < 1024) return bytes + ' B'
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB'
    if (bytes < 1024 * 1024 * 1024) return (bytes / (1024 * 1024)).toFixed(1) + ' MB'
    return (bytes / (1024 * 1024 * 1024)).toFixed(1) + ' GB'
  }

  // 버전 정보에서 매뉴얼 파일 ID 가져오기
  const manuals = React.useMemo(() => {
    const installationFileId = versionInfo?.installationManualFileId
    const usageFileId = versionInfo?.usageManualFileId
    
    console.log('manuals 생성:', { installationFileId, usageFileId, versionInfo })
    
    return [
      {
        id: 'installation',
        title: '설치 가이드',
        description: '카카오톡 단체발송 프로그램 설치 방법',
        fileId: installationFileId || null,
      },
      {
        id: 'usage',
        title: '사용법 가이드',
        description: '카카오톡 단체발송 프로그램 사용 방법',
        fileId: usageFileId || null,
      }
    ]
  }, [versionInfo])

  const openManual = (manual) => {
    setSelectedManual(manual)
    setManualOpen(true)
  }

  const issueInstallCode = async () => {
    setInstallCodeLoading(true)
    try {
      const res = await api.post('/external/install-codes/kakao-sender/issue')
      const data = res?.data?.data ?? res?.data
      setInstallCodeInfo(data)
      return data
    } catch (e) {
      const message = e?.response?.data?.message || '설치코드를 발급할 수 없습니다. 다시 시도해주세요.'
      show(message, 'error')
      return null
    } finally {
      setInstallCodeLoading(false)
    }
  }

  const openInstallCodeModal = async () => {
    setInstallCodeOpen(true)
    setInstallCodeInfo(null)
    await issueInstallCode()
  }

  return (
    <div className="space-y-6">
      <div>
        <div className="flex flex-wrap items-center gap-2">
          <h1 className="text-2xl font-bold">카카오톡 단체발송</h1>
          <MenuGuideButton slug="kakao-bulk-sender" title="카카오톡 단체발송 가이드" />
        </div>
      </div>

      {/* 간략한 설명 섹션 - 히든 */}
      {false && (
        <div className="rounded border bg-blue-50 p-4">
          <h2 className="text-lg font-semibold mb-2">카카오톡 단체발송이란?</h2>
          <p className="text-sm text-slate-700 mb-3">
            카카오톡 단체발송 프로그램은 설계EZ 플랫폼과 연동하여 고객에게 카카오톡 알림톡을 대량으로 발송할 수 있는 Windows 프로그램입니다.
          </p>
          <div className="text-sm text-slate-700">
            <div className="font-semibold mb-1">주요 기능:</div>
            <ul className="list-disc list-inside space-y-1">
              <li>템플릿 기반 자동 발송</li>
              <li>수신자 목록 관리</li>
              <li>발송 이력 조회</li>
              <li>실시간 발송 상태 확인</li>
            </ul>
          </div>
        </div>
      )}

      {/* 좌: 매뉴얼 + 다운로드 / 우: 메시지 템플릿 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-start">
        <div className="space-y-4 min-w-0">
          <div className="rounded border p-3">
            <h2 className="text-base font-semibold mb-2">매뉴얼</h2>
            {manuals.length > 0 ? (
              <div className="space-y-2">
                {manuals.map(manual => (
                  <button
                    key={manual.id}
                    onClick={() => manual.fileId && openManual(manual)}
                    disabled={!manual.fileId}
                    className={`w-full rounded border p-2 text-left transition-colors ${
                      manual.fileId
                        ? 'hover:bg-slate-50'
                        : 'opacity-50 cursor-not-allowed'
                    }`}
                  >
                    <div className="text-sm font-medium">{manual.title}</div>
                    <div className="text-xs text-slate-600 mt-0.5">
                      {manual.fileId ? manual.description : '매뉴얼 파일이 등록되지 않았습니다.'}
                    </div>
                  </button>
                ))}
              </div>
            ) : (
              <div className="text-sm text-slate-600">매뉴얼이 등록되지 않았습니다.</div>
            )}
          </div>

          <div className="rounded border p-3">
            <h2 className="text-base font-semibold mb-2">프로그램 다운로드</h2>
            {versionLoading ? (
              <div className="text-sm text-slate-600">버전 정보를 불러오는 중...</div>
            ) : versionInfo ? (
              <div className="space-y-2">
                <div>
                  <div className="text-sm font-medium">최신 버전: {versionInfo.version}</div>
                  {versionInfo.fileSize && (
                    <div className="text-xs text-slate-600 mt-0.5">
                      파일 크기: {formatFileSize(versionInfo.fileSize)}
                    </div>
                  )}
                  {versionInfo.createdAt && (
                    <div className="text-xs text-slate-600 mt-0.5">
                      배포일: {formatDateTimeMinute(versionInfo.createdAt)}
                    </div>
                  )}
                </div>
                <button
                  onClick={handleDownload}
                  className="w-full rounded bg-brand-navy text-white px-4 py-1.5 text-sm hover:bg-brand-navy/90"
                >
                  다운로드
                </button>
                <button
                  onClick={openInstallCodeModal}
                  className="w-full rounded border border-brand-navy text-brand-navy px-4 py-1.5 text-sm hover:bg-slate-50"
                >
                  설치코드 확인하기
                </button>
                {versionInfo.releaseNotes && (
                  <div className="rounded bg-slate-50 p-2 text-xs">
                    <div className="font-semibold mb-0.5">릴리스 노트:</div>
                    <div className="text-slate-700 whitespace-pre-line">
                      {versionInfo.releaseNotes}
                    </div>
                  </div>
                )}
                {versionInfo.isCritical && (
                  <div className="rounded bg-red-50 border border-red-200 p-2 text-xs text-red-700">
                    ⚠️ 필수 업데이트: 이 버전은 반드시 설치해야 합니다.
                  </div>
                )}
              </div>
            ) : (
              <div className="text-sm text-slate-600">
                버전 정보를 불러올 수 없습니다. 나중에 다시 시도해주세요.
              </div>
            )}
          </div>
        </div>

        <div className="rounded border p-4 min-w-0">
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between mb-3">
          <h2 className="text-lg font-semibold">메시지 템플릿</h2>
          <div className="flex items-center gap-2">
            <label htmlFor="kakao-template-category" className="text-xs text-slate-600 whitespace-nowrap">카테고리</label>
            <select
              id="kakao-template-category"
              className="rounded border px-2 py-1.5 text-sm min-w-[10rem]"
              value={templateCategory}
              onChange={(e) => setTemplateCategory(e.target.value)}
            >
              <option value="">전체</option>
              {templateCategories.map((c) => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
          </div>
        </div>
        {templatesLoading ? (
          <div className="text-sm text-slate-600">메시지 템플릿을 불러오는 중...</div>
        ) : templates.length > 0 ? (
          <div className="space-y-2 max-h-[min(70vh,40rem)] overflow-y-auto pr-1">
            {templates.map(template => {
              const linkedImgIds = Array.isArray(template?.linkedLibraryImageIds)
                ? template.linkedLibraryImageIds.map((x) => Number(x)).filter((n) => Number.isFinite(n) && n >= 1)
                : []
              return (
              <div key={template.id} className="rounded border bg-white p-3">
                <div className="flex flex-wrap items-center gap-2">
                  <div className="font-medium">{template.title || '제목 없음'}</div>
                  {template.category ? (
                    <span className="text-xs rounded-full bg-slate-100 text-slate-700 px-2 py-0.5">{template.category}</span>
                  ) : null}
                </div>
                {template.content && (
                  <div className="text-sm text-slate-600 mt-1 line-clamp-2">
                    {template.content}
                  </div>
                )}
                {template.updatedAt && (
                  <div className="text-xs text-slate-400 mt-1">
                    최종 수정: {formatDateTimeMinute(template.updatedAt)}
                  </div>
                )}
                <div className="mt-2 flex flex-wrap items-center gap-2">
                  <button
                    type="button"
                    onClick={() => setTemplatePreview(template)}
                    className="rounded border border-slate-300 bg-white px-2.5 py-1 text-xs text-slate-700 hover:bg-slate-50"
                  >
                    전체보기
                  </button>
                  <button
                    type="button"
                    onClick={() => void copyTemplateBody(template)}
                    className="rounded border border-slate-300 bg-white px-2.5 py-1 text-xs text-slate-700 hover:bg-slate-50"
                  >
                    본문 복사
                  </button>
                  {linkedImgIds.length > 0 && (
                    <button
                      type="button"
                      onClick={() => setTemplateImageView({ ...template, _linkedIds: linkedImgIds })}
                      className="rounded border border-sky-300 bg-sky-50 px-2.5 py-1 text-xs text-sky-900 hover:bg-sky-100"
                    >
                      이미지 보기
                    </button>
                  )}
                  {!user?.name ? (
                    <span className="text-[11px] text-amber-700">프로필에 이름이 없으면 OOO 치환이 되지 않습니다.</span>
                  ) : null}
                </div>
              </div>
            )})}
          </div>
        ) : (
          <div className="text-sm text-slate-600">
            메시지 템플릿이 없습니다.
          </div>
        )}
        </div>
      </div>

      {/* 매뉴얼 팝업 */}
      {manualOpen && selectedManual && (
        <ManualModal
          manual={selectedManual}
          onClose={() => {
            setManualOpen(false)
            setSelectedManual(null)
          }}
        />
      )}

      {installCodeOpen && (
        <InstallCodeModal
          installCodeInfo={installCodeInfo}
          loading={installCodeLoading}
          onClose={() => {
            setInstallCodeOpen(false)
            setInstallCodeInfo(null)
          }}
          onReissue={issueInstallCode}
          showToast={show}
        />
      )}

      {templatePreview && (
        <MessageTemplatePreviewModal
          template={templatePreview}
          displayBody={applyPlannerNameToMessageTemplate(
            templatePreview.content != null ? String(templatePreview.content) : '',
            user?.name || '',
          )}
          onClose={() => setTemplatePreview(null)}
          onCopy={() => void copyTemplateBody(templatePreview)}
        />
      )}

      {templateImageView && Array.isArray(templateImageView._linkedIds) && templateImageView._linkedIds.length > 0 && (
        <LibraryMappedImagesModal
          imageIds={templateImageView._linkedIds}
          title={templateImageView.title ? `이미지 · ${templateImageView.title}` : '연결된 이미지'}
          onClose={() => setTemplateImageView(null)}
        />
      )}
    </div>
  )
}

function MessageTemplatePreviewModal({ template, displayBody, onClose, onCopy }) {
  return (
    <div className="fixed inset-0 bg-black/30 flex items-center justify-center p-4 z-50" onClick={onClose}>
      <div
        className="w-full max-w-2xl max-h-[min(90vh,36rem)] rounded bg-white shadow-lg flex flex-col overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-3 p-4 border-b shrink-0">
          <div className="min-w-0">
            <h2 className="text-base font-semibold text-slate-900 leading-snug">{template.title || '제목 없음'}</h2>
            {template.category ? (
              <span className="inline-block mt-1 text-xs rounded-full bg-slate-100 text-slate-700 px-2 py-0.5">
                {template.category}
              </span>
            ) : null}
            <p className="mt-1 text-[11px] text-slate-500">
              미리보기는 「본문 복사」와 동일합니다. 프로필 이름이 있으면 OOO 등 치환에 반영됩니다.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-slate-400 hover:text-slate-600 text-2xl leading-none shrink-0"
            aria-label="닫기"
          >
            ×
          </button>
        </div>
        <div className="flex-1 min-h-0 overflow-y-auto p-4">
          <pre className="whitespace-pre-wrap break-words text-sm text-slate-800 font-sans leading-relaxed">
            {displayBody || '(내용 없음)'}
          </pre>
        </div>
        <div className="flex flex-wrap justify-end gap-2 p-4 border-t bg-slate-50 shrink-0">
          <button
            type="button"
            onClick={onCopy}
            className="rounded border border-slate-300 bg-white px-3 py-1.5 text-sm text-slate-700 hover:bg-slate-100"
          >
            본문 복사
          </button>
          <button
            type="button"
            onClick={onClose}
            className="rounded bg-brand-navy text-white px-4 py-1.5 text-sm hover:bg-brand-navy/90"
          >
            닫기
          </button>
        </div>
      </div>
    </div>
  )
}

function ManualModal({ manual, onClose }) {
  return (
    <div className="fixed inset-0 bg-black/30 flex items-center justify-center p-4 z-50" onClick={onClose}>
      <div 
        className="w-full max-w-3xl max-h-[90vh] rounded bg-white shadow-lg overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between p-4 border-b">
          <h2 className="text-lg font-semibold">{manual.title}</h2>
          <button
            onClick={onClose}
            className="text-slate-400 hover:text-slate-600 text-2xl leading-none"
          >
            ×
          </button>
        </div>
        <div className="overflow-y-auto p-6 flex-1">
          {manual.fileId ? (
            <div className="space-y-4">
              <p className="text-sm text-slate-600">{manual.description}</p>
              <div className="flex gap-2">
                <button
                  onClick={async () => {
                    try {
                      const res = await api.get(`/files/${manual.fileId}/preview`, { responseType: 'blob' })
                      const url = URL.createObjectURL(res.data)
                      window.open(url, '_blank')
                      setTimeout(() => URL.revokeObjectURL(url), 60000)
                    } catch (e) {
                      alert('파일을 열 수 없습니다.')
                    }
                  }}
                  className="rounded border px-4 py-2 text-sm hover:bg-slate-50"
                >
                  미리보기
                </button>
                <button
                  onClick={async () => {
                    try {
                      const res = await api.get(`/files/${manual.fileId}/preview`, { responseType: 'blob' })
                      const url = URL.createObjectURL(res.data)
                      const a = document.createElement('a')
                      a.href = url
                      a.download = `${manual.title}.pdf`
                      a.click()
                      setTimeout(() => URL.revokeObjectURL(url), 60000)
                    } catch (e) {
                      alert('파일을 다운로드할 수 없습니다.')
                    }
                  }}
                  className="rounded border px-4 py-2 text-sm hover:bg-slate-50"
                >
                  다운로드
                </button>
              </div>
            </div>
          ) : (
            <div className="text-sm text-slate-600">
              매뉴얼 파일이 등록되지 않았습니다.
            </div>
          )}
        </div>
        <div className="p-4 border-t flex justify-end">
          <button
            onClick={onClose}
            className="rounded bg-brand-navy text-white px-4 py-2 text-sm"
          >
            닫기
          </button>
        </div>
      </div>
    </div>
  )
}

function InstallCodeModal({ installCodeInfo, loading, onClose, onReissue, showToast }) {
  const copyInstallCode = async () => {
    if (!installCodeInfo?.installCode) return
    try {
      await navigator.clipboard.writeText(installCodeInfo.installCode)
      showToast('설치코드를 복사했습니다.', 'success')
    } catch {
      showToast('설치코드를 복사하지 못했습니다.', 'error')
    }
  }

  return (
    <div className="fixed inset-0 bg-black/30 flex items-center justify-center p-4 z-50" onClick={onClose}>
      <div
        className="w-full max-w-lg rounded bg-white shadow-lg overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between p-4 border-b">
          <h2 className="text-lg font-semibold">설치코드 확인</h2>
          <button
            onClick={onClose}
            className="text-slate-400 hover:text-slate-600 text-2xl leading-none"
          >
            ×
          </button>
        </div>

        <div className="p-5 space-y-4">
          <div className="rounded border bg-slate-50 p-3 text-sm text-slate-700">
            프로그램 설치 후 최초 실행 시 아래 설치코드를 1회 입력하세요. 등록이 완료되면 이후에는 다시 입력할 필요가 없습니다.
          </div>

          {loading ? (
            <div className="rounded border p-4 text-sm text-slate-600">
              설치코드를 발급하는 중...
            </div>
          ) : installCodeInfo ? (
            <div className="space-y-3">
              <div className="rounded border p-4">
                <div className="text-xs text-slate-500 mb-1">설치코드</div>
                <div className="text-2xl font-bold tracking-[0.2em] text-brand-navy break-all">
                  {installCodeInfo.installCode}
                </div>
                <div className="mt-2 text-xs text-slate-500">
                  만료 시간: {installCodeInfo.expiresAt ? formatDateTimeMinute(installCodeInfo.expiresAt) : '-'}
                </div>
                <div className="mt-1 text-xs text-slate-500">
                  적용 조직: {installCodeInfo.orgName || '-'}
                </div>
              </div>

              <div className="rounded border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800">
                설치코드는 1회용입니다. 다시 발급하면 이전 코드는 더 이상 사용할 수 없습니다.
              </div>

              <div className="flex flex-wrap gap-2">
                <button
                  onClick={copyInstallCode}
                  className="rounded border px-4 py-2 text-sm hover:bg-slate-50"
                >
                  코드 복사
                </button>
                <button
                  onClick={onReissue}
                  className="rounded border px-4 py-2 text-sm hover:bg-slate-50"
                >
                  새 코드 발급
                </button>
              </div>

              <div className="rounded border bg-white p-3 text-sm text-slate-700">
                <div className="font-medium mb-1">사용 방법</div>
                <ol className="list-decimal list-inside space-y-1 text-sm text-slate-600">
                  <li>프로그램을 다운로드하고 설치합니다.</li>
                  <li>최초 실행 시 설치코드를 입력합니다.</li>
                  <li>등록이 완료되면 프로그램이 인증 정보를 자동 저장합니다.</li>
                </ol>
              </div>
            </div>
          ) : (
            <div className="rounded border p-4 text-sm text-slate-600">
              설치코드를 불러오지 못했습니다.
            </div>
          )}
        </div>

        <div className="p-4 border-t flex justify-end">
          <button
            onClick={onClose}
            className="rounded bg-brand-navy text-white px-4 py-2 text-sm"
          >
            닫기
          </button>
        </div>
      </div>
    </div>
  )
}
